/**
 * Tenant theme → this app's design tokens.
 *
 * Embedded in a Rumio site, the host hands over its palette twice: as the
 * `theme` claim of the session JWT, and live as a `rumio:design` message
 * whenever the tenant's theme or light/dark mode changes (apps platform
 * contract v1.1 §3). Both carry the same nested shape with CSS colors as
 * values. The app's own tokens (styles/globals.css) are HSL channels, so each
 * color is converted and written onto <html> as a custom property.
 *
 * A standalone account has no host and no theme; the defaults in globals.css
 * are what it sees.
 */

import { isFromParent } from "./iframe-bridge";

type Shades = Record<string, string | undefined>;

interface RumioTheme {
  primary?: Shades;
  text?: Shades;
  bg?: Shades;
  fonts?: Shades;
  dark_mode?: boolean;
}

function parseRgb(color: string): [number, number, number] | null {
  const c = color.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(c)?.[1];
  if (hex) {
    const full = hex.length === 3 ? hex.replace(/./g, (ch) => ch + ch) : hex;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
  }
  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/.exec(c);
  if (rgb) {
    const parts = rgb.slice(1, 4).map(Number);
    return parts.every((n) => n <= 255) ? (parts as [number, number, number]) : null;
  }
  return null;
}

/** A CSS color as the `H S% L%` channels the tokens hold; null when it isn't a hex or rgb() color. */
export function toHslChannels(color: string): string | null {
  const rgb = parseRgb(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((n) => n / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d > 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
function luminance(color: string): number | null {
  const rgb = parseRgb(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((n) => {
    const v = n / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export interface ThemeTokens {
  /** Custom property name (without `--`) → value. */
  vars: Record<string, string>;
  dark: boolean;
}

/** Which of this app's tokens a host theme sets. Anything the theme lacks keeps its default. */
export function themeToTokens(input: unknown): ThemeTokens {
  const theme = (input && typeof input === "object" ? input : {}) as RumioTheme;
  const dark = theme.dark_mode === true;
  const vars: Record<string, string> = {};
  const color = (value: string | undefined, ...names: string[]) => {
    const hsl = typeof value === "string" ? toHslChannels(value) : null;
    if (hsl) for (const name of names) vars[name] = hsl;
  };

  // The page blends into the host around it; cards sit one step off it in the dark.
  color(theme.bg?.primary, "background");
  color(dark ? theme.bg?.secondary : theme.bg?.primary, "card", "popover");
  color(theme.bg?.tertiary, "muted", "secondary", "accent");
  color(
    theme.text?.primary,
    "foreground",
    "card-foreground",
    "popover-foreground",
    "secondary-foreground",
    "accent-foreground",
  );
  color(theme.text?.secondary, "muted-foreground");
  color(theme.primary?.["100"], "border");
  color(theme.primary?.["200"], "input");
  color(theme.primary?.["500"] ?? theme.primary?.["600"], "ring");

  const primary = theme.primary?.["600"];
  color(primary, "primary");
  const lum = typeof primary === "string" ? luminance(primary) : null;
  // Whichever of white and near-black reads better on the tenant's primary.
  if (lum !== null) vars["primary-foreground"] = lum > 0.179 ? "0 0% 9%" : "0 0% 100%";

  const sans = theme.fonts?.sans;
  if (typeof sans === "string" && sans.trim()) vars["font-sans"] = sans.trim();

  return { vars, dark };
}

let applied: string[] = [];

/** Put a host theme on the page, replacing whatever an earlier one set. */
export function applyTheme(input: unknown, root: HTMLElement = document.documentElement): void {
  const { vars, dark } = themeToTokens(input);
  for (const name of applied) root.style.removeProperty(`--${name}`);
  applied = Object.keys(vars);
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(`--${name}`, value);
  root.classList.toggle("dark", dark);
}

/** Live theme changes from the host (`rumio:design`). Returns the disposer. */
export function listenForDesign(handler: (tokens: unknown) => void): () => void {
  function onMessage(ev: MessageEvent) {
    if (!isFromParent(ev)) return;
    const data = ev.data as { type?: unknown; tokens?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== "rumio:design") return;
    if (data.tokens && typeof data.tokens === "object") handler(data.tokens);
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
